import React, { createContext, FC, useCallback, useMemo, useState } from "react";
import { GenerateODataFilter } from "../helpers/odataFunctions";
import { ModelPlacement } from "../models/StillImageConfiguration";
import { BomModelEditDTO, BomModelService, BomModelViewDTO, ConfigurationAssets, MaterialService, MaterialViewDTO, MetadataViewDTO, ModelPackService, ModelPackViewDTO, ModelPlacementPointDTO, ModelService, ModelSurfaceDTO, ModelViewDTO, ScenePlacementpointDTO, SceneSurfaceDTO } from "../openapi/requests";
import { Asset, ItemConfigurationValue, MaterialAsset, MaterialPoint, ModelAsset, ModelConfiguration, ModelpackAsset, ModelPoint, ModifierAsset, Point, SurfaceConfiguration } from "./SceneDesignerContext";
import { Filter } from "../types/types";



const rootPlacement: ItemConfigurationValue<ModelViewDTO, ScenePlacementpointDTO > = {
    parent: undefined, 
    placement: {
        id: 0, 
        isRequired: true,
        label: 'root',
        modelSelectionFilter: '{IsWhitemodel = true}',
        name: '',
        objectSelector: '',
        sort: 0,
        useModelpack: false
    }, 
    values: []
}

export const emptyConfiguration: ModelConfiguration =  new ModelConfiguration([rootPlacement]);

export interface BomDesignerContextState {
    loading: boolean;
    hasChanges: boolean;
    isNew: boolean;
    configuration: ModelConfiguration;
    changeProduct: (point: Point, products: Asset[], force: boolean) => Promise<boolean>;
    title: string;
    setTitle: (title: string) => void;
    isEnabled: boolean;
    setIsEnabled: (value: boolean) => void;
    tags: Array<string>;
    addTag: (tag: string) => void;
    removeTag: (tag: string) => void;
    metadata: Array<MetadataViewDTO>;
    addMetadata: (data: MetadataViewDTO) => void;
    removeMetadata: (data: MetadataViewDTO) => void;
    editMetadata: (index: number, data: MetadataViewDTO) => void;
    addExternalPoint: (point: Point) => void;
    removeExternalPoint: (point: Point) => void;
    points: Array<Point>;
    save: () => Promise<void>;
    createNew: () => void;
    createFromConfig: (config: ModelConfiguration) => void;
    load: (id: number) => void;
};

const contextDefaultValues: BomDesignerContextState = {
    loading: false,
    hasChanges: false,
    isNew: false,
    configuration: emptyConfiguration,
    changeProduct: () => Promise.reject(),
    points: [],
    createNew: () => {},
    createFromConfig: () => {},
    load: () => {},
    save: () => Promise.reject(),
    addExternalPoint: () => {},
    addMetadata: () => {},
    addTag: () => {},
    editMetadata: () => {},
    metadata: [],
    removeExternalPoint: () => {},
    removeMetadata: () => {},
    removeTag: () => {},
    setTitle: () => {},
    tags: [],
    title: '',
    isEnabled: false,
    setIsEnabled: () => {},
};

export const BomDesignerContext = createContext<BomDesignerContextState>(
    contextDefaultValues
);

export const DefaultMetadataFields = ["product_type_primary", "product_type_secondary", "ean", "size_height", "size_length", "size_width"]

export const BomDesignerProvider: FC<{ children: React.ReactNode, odataFilters: Filter[] }> = ({ children, odataFilters }) => {

    const [loading, setLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [configuration, setConfiguration] = useState(emptyConfiguration);
    const [model, setModel] = useState<BomModelViewDTO | undefined>();
    const isNew = model === undefined;
    const [title, internalSetTitle] = useState("");
    const [isEnabled, internalSetIsEnabled] = useState(false);
    const [tags, setTags] = useState<string[]>([]);
    const [metadata, setMetadata] = useState<MetadataViewDTO[]>([]);

    const setTitle = useCallback((title: string) => {
        setHasChanges(true);
        internalSetTitle(title);
    }, []);

    const setIsEnabled = useCallback((value: boolean) => {
        setHasChanges(true);
        internalSetIsEnabled(value);
    }, []);

    const addTag = useCallback((tag: string) => {
        if(!tags.includes(tag)){
            setHasChanges(true);
            setTags([...tags, tag]);
        }
    }, [tags]);

    const removeTag = useCallback((tag: string) => {
        if(tags.includes(tag)){
            setHasChanges(true);
            setTags(tags.filter(e => e !== tag));
        }
    }, [tags]);

    const addMetadata = useCallback((value: MetadataViewDTO) => {
        if(!metadata.some(e => e.name === value.name && e.value === value.value)){
            setHasChanges(true);
            setMetadata([...metadata, value]);
        }
    }, [metadata]);

    const removeMetadata = useCallback((value: MetadataViewDTO) => {
        if(metadata.some(e => e.name === value.name && e.value === value.value && !e.locked)){
            setHasChanges(true);
            setMetadata(metadata.filter(e => e.name !== value.name || e.value !== value.value));
        }
    }, [metadata]);

    const editMetadata = useCallback((index: number, value: MetadataViewDTO) => {
        if(index >= metadata.length){
            return;
        }

        if(!metadata[index].locked && (metadata[index].name !== value.name || metadata[index].value !== value.value)){
            setHasChanges(true);
            setMetadata(metadata.map((e,i) => i === index ? value : e));
        }
    }, [metadata]);

    const points = useMemo(() => {
        const result: Point[] = [];

        const flatten = (model: ModelConfiguration, parent: Point) => {

            for (let i = 0; i < model.surfaces.length; i++) {
                var MaterialPoint: MaterialPoint = {
                    configuration: model.surfaces[i],
                    isRequired: true,
                    label: model.surfaces[i].label,
                    type: 'material',
                    offbrandProduct: false,
                    offStylingProduct: false,
                    view: parent.view,
                    parent: parent,
                    children: [],
                    hidden: model.surfaces[i].hideInMoodboard,
                };
                parent.children.push(MaterialPoint);
                result.push(MaterialPoint);
            }

            for (let i = 0; i < model.models.length; i++) {
                var modelPoint: ModelPoint = {
                    configuration: model.models[i],
                    isRequired: model.models[i].values[0].placement.isRequired,
                    label: model.models[i].label,
                    offbrandProduct: false,
                    offStylingProduct: false,
                    type: 'model',
                    view: parent.view,
                    parent: parent,
                    children: [],
                    hidden: model.models[i].hideInMoodboard,
                };

                const value = modelPoint.configuration.value as ModelViewDTO;

                if (value && value.isWhitemodel) {
                    modelPoint.bomData = {points: value.hotspots};
                }
                parent.children.push(modelPoint);
                result.push(modelPoint);
                flatten(model.models[i], modelPoint);
            }
        }

        let point: Point = {
            configuration: configuration,
            isRequired: configuration.isRequired,
            label: configuration.label,
            offbrandProduct: false,
            offStylingProduct: false,
            type: 'model',
            parent: undefined,
            children: [],
            hidden: configuration.hideInMoodboard,
        };

        const value = point.configuration.value as ModelViewDTO;

        if (value && value.isWhitemodel) {
            point.bomData = {points: value.hotspots};
        }

        result.push(point);

        flatten(configuration, point);

        return result;
    }, [configuration]);

    const addExternalPoint = useCallback((point: Point) => {
        if(point.hidden){
            point.hidden = false;
            point.configuration.hideInMoodboard = false;
            setConfiguration(configuration.clone());
            setHasChanges(true);
        }
    }, [configuration]);

    const removeExternalPoint = useCallback((point: Point) => {
        if(!point.hidden){
            point.hidden = true;
            point.configuration.hideInMoodboard = true;
            setConfiguration(configuration.clone());
            setHasChanges(true);
        }
    }, [configuration]);

    const readConfiguration = useCallback(async (model: BomModelViewDTO): Promise<void> => {
        setModel(model);
        setHasChanges(false);

        var config = JSON.parse(model.configuration) as ModelPlacement;
        var configItems: ConfigurationAssets = {materials: [], modelpacks: [], models: [], modifiers: []};
        
        try {
            configItems = await BomModelService.getBomModelAssets(model.id);
        } catch {}


        setConfiguration(ModelConfiguration.deserialize([rootPlacement], config, undefined, configItems));
        internalSetTitle(model.title);
        internalSetIsEnabled(model.isEnabled);
        setTags(model.tags);
        setMetadata(model.metadata);
    }, []);

    const save = useCallback(async () => {
        setLoading(true);
        
        const modelData: BomModelEditDTO = {
            approvalStatus: model?.approvalStatus ?? 0,
            clientId: configuration.value?.clientId ?? 0,
            complexity: 0,
            id: 0,
            metadata: metadata,
            tags: tags,
            title: title,
            isEnabled: isEnabled,
            sourceType: 0,
            configuration: JSON.stringify(configuration.serialize(true, true)),
            modelId: configuration.value?.id ?? 0,
        }

        if(model === undefined){
            const result = await BomModelService.postBomModel(modelData);
            readConfiguration(result).then(() => {
                setLoading(false);
            });
        }else{
            const result = await BomModelService.putBomModel(model.id, modelData);
            readConfiguration(result).then(() => {
                setLoading(false);
            });
        }
    }, [model, metadata, tags, title, isEnabled, configuration, readConfiguration]);

    const reset = useCallback(() => {
        setModel(undefined);
        setHasChanges(false);
        internalSetTitle("");
        setConfiguration(emptyConfiguration);
        setMetadata([{name: "ean", value: "", locked: false}]);
        setTags([]);
    }, []);

    const load = useCallback(async (id: number) => {
        setLoading(true);
        reset();

        const _load = async () => {
            setConfiguration(emptyConfiguration);

            let model = await BomModelService.getBomModel1(id);

            if(model){
                readConfiguration(model).then(() => {
                    setLoading(false);
                });
            }
        }

        await _load();

    }, [readConfiguration, reset]);

    const createNew = useCallback(() => {
        reset();
        internalSetIsEnabled(true);
    }, [reset]);

    const createFromConfig = useCallback((config: ModelConfiguration) => {
        reset();

        if(config.value){
            config = config.setValue([{placement: rootPlacement.placement, values: [config.value], parent: rootPlacement.parent}]);
        }else{
            config = emptyConfiguration;
        }

        internalSetIsEnabled(true);
        setMetadata([{name: "ean", value: "", locked: false}, ...(config.value?.metadata ?? []).filter(e => e.name !== "ean")]);
        setConfiguration(config);
    }, [reset]);

    const changeProduct = useCallback(async (point: Point, products: Asset[], force: boolean) => {

        var newConfig = point.configuration;
        
        if (point.type === "material") {
            const placements = point.configuration.values.map(v => ({
                placement: v.placement,
                parent: v.parent,
                values: products.filter(e => e.validFor.some(e => e.name === v.placement.name)) as MaterialAsset[]
            }));
            newConfig = point.configuration.setValue(placements);
        } else if (point.type === 'modifier') {
            const placements = point.configuration.values.map(v => ({
                placement: v.placement,
                parent: v.parent,
                values: products.filter(e => e.validFor.some(e => e.name === v.placement.name)) as ModifierAsset[]
            }));
            newConfig = point.configuration.setValue(placements);
        } else if (point.type === 'model') {
            let oldConfig = point.configuration;

            const placements = oldConfig.values.map(v => ({
                placement: v.placement,
                parent: v.parent,
                values: products.filter(e => e.validFor.some(e => e.name === v.placement.name)) as (ModelAsset | ModelpackAsset)[]
            }));

            newConfig = oldConfig.setValue(placements);
            newConfig.models = [];
            newConfig.surfaces = [];

            let placementPointsByLabel: { [key: string]: {placement: ModelPlacementPointDTO, parent: ModelViewDTO | ModelPackViewDTO}[] } = {};
            let surfacesByLabel: { [key: string]: {placement: ModelSurfaceDTO, parent: ModelViewDTO | ModelPackViewDTO}[] } = {};
            const modelsAdded: number[] = [];


            for (let i = 0; i < newConfig.uniqueValues.length; i++) {
                const asset = newConfig.uniqueValues[i];
                
                if((asset as ModelPackViewDTO).models){
                    let modelpack = asset as ModelPackViewDTO;
                    var mastermodelID = modelpack.models[0].modelId;

                    var master = modelpack.models.find(e => e.isMasterModel);
                    if (master) {
                        mastermodelID = master.modelId;
                    }

                    let masterModel = await ModelService.getModel1(mastermodelID);

                    if (masterModel && !modelsAdded.includes(masterModel.id)) {
                        masterModel.placementpoints.forEach(p => {
                            if (placementPointsByLabel[p.label]) {
                                placementPointsByLabel[p.label].push({placement: p, parent: asset});
                            } else {
                                placementPointsByLabel[p.label] = [{placement: p, parent: asset}];
                            }
                        });
                        masterModel.surfaces.forEach(s => {
                            if (surfacesByLabel[s.label]) {
                                surfacesByLabel[s.label].push({placement: s, parent: asset});
                            } else {
                                surfacesByLabel[s.label] = [{placement: s, parent: asset}];
                            }
                        });
                        modelsAdded.push(masterModel.id);
                    }
                }else{
                    let model = asset as ModelViewDTO;

                    if (!modelsAdded.includes(model.id)) {
                        model.placementpoints.forEach(p => {
                            if (placementPointsByLabel[p.label]) {
                                placementPointsByLabel[p.label].push({placement: p, parent: asset});
                            } else {
                                placementPointsByLabel[p.label] = [{placement: p, parent: asset}];
                            }
                        });
                        model.surfaces.forEach(s => {
                            if (surfacesByLabel[s.label]) {
                                surfacesByLabel[s.label].push({placement: s, parent: asset});
                            } else {
                                surfacesByLabel[s.label] = [{placement: s, parent: asset}];
                            }
                        });
                        modelsAdded.push(model.id);
                    }
                }
            }

            for (const key in surfacesByLabel) {
                if (Object.prototype.hasOwnProperty.call(surfacesByLabel, key)) {
                    const surfaces = surfacesByLabel[key];

                    const oldSurfaceConfig = oldConfig.surfaces.find(e => e.placement.label === surfaces[0].placement.label);
                    const placements: ItemConfigurationValue<MaterialViewDTO, SceneSurfaceDTO | ModelSurfaceDTO>[] = surfaces.map(e => ({placement: e.placement, parent: e.parent, values: []}));

                    if(oldSurfaceConfig !== undefined && oldSurfaceConfig.uniqueValues.length > 0){

                        for (let i = 0; i < surfaces.length; i++) {
                            const surface = surfaces[i].placement;
                            let validMaterials = await MaterialService.getMaterial(surface.materialSelectionFilter, undefined, undefined, GenerateODataFilter(odataFilters));
                            
                            let placement = placements.find(e => e.placement.name === surface.name);
                            if(placement){
                                placement.values = oldSurfaceConfig.uniqueValues.filter(v => validMaterials.value.some(e => e.id === v.id));
                            }
                        }
                    }

                    const newSurfaceConfig = new SurfaceConfiguration(placements);

                    if(oldSurfaceConfig && oldSurfaceConfig.uniqueValues.length > 0 && newSurfaceConfig.uniqueValues.length === 0){
                        if(!force){
                            return false;
                        }
                    }

                    newConfig.surfaces.push(newSurfaceConfig);
                }
            }

            for (const key in placementPointsByLabel) {
                if (Object.prototype.hasOwnProperty.call(placementPointsByLabel, key)) {
                    const points = placementPointsByLabel[key];

                    const oldModelConfig = oldConfig.models.find(e => e.placement.label === points[0].placement.label);
                    const placements: ItemConfigurationValue<ModelViewDTO | ModelPackViewDTO, ScenePlacementpointDTO | ModelPlacementPointDTO>[] = points.map(e => ({placement: e.placement, parent: e.parent, values: []}));

                    if(oldModelConfig !== undefined && oldModelConfig.uniqueValues.length > 0){
                        for (let i = 0; i < points.length; i++) {
                            const point = points[i].placement;

                            if(point.useModelpack){
                                let placement = placements.find(e => e.placement.name === point.name);
                                if(placement){
                                    let validModelpacks = await ModelPackService.getModelPack(point.modelSelectionFilter, undefined, undefined, GenerateODataFilter(odataFilters));
                                    placement.values = oldModelConfig.uniqueValues.filter(v => validModelpacks.value.some(e => e.id === v.id));
                                }
                            }else{
                                let placement = placements.find(e => e.placement.name === point.name);
                                if(placement){
                                    let validModels = await ModelService.getModel(point.modelSelectionFilter, undefined, undefined, GenerateODataFilter(odataFilters));
                                    placement.values = oldModelConfig.uniqueValues.filter(v => validModels.value.some(e => e.id === v.id));
                                }
                            }
                        }
                    }

                    let newModelConfig = oldModelConfig ? oldModelConfig?.clone() : new ModelConfiguration(placements);
                    newModelConfig = newModelConfig.setValue(placements);

                    if(oldModelConfig && oldModelConfig.uniqueValues.length > 0 && newModelConfig.uniqueValues.length === 0){
                        if(!force){
                            return false;
                        }
                    }

                    if(newModelConfig.value === undefined){
                        newModelConfig.models = [];
                        newModelConfig.surfaces = [];
                    }

                    newConfig.models.push(newModelConfig);
                }
            }
        }


        if (point.parent === undefined) {
            if (newConfig instanceof ModelConfiguration) {

                if(!metadata.some(e => e.name !== "ean")){
                    setMetadata([...metadata, ...(newConfig.value?.metadata ?? []).filter(e => e.name !== "ean")]);
                }

                setConfiguration(newConfig);
            }
        } else {
            let parent = point.parent.configuration;

            if (parent instanceof ModelConfiguration) {
                if (newConfig instanceof ModelConfiguration) {
                    var modelConfig = newConfig;
                    modelConfig.hideInMoodboard = true;
                    parent.models = parent.models.map(e => e.placement.name === point.configuration.placement.name ? modelConfig : e);
                    setConfiguration(configuration.clone());
                }
                if (newConfig instanceof SurfaceConfiguration) {
                    var materialConfig = newConfig;
                    materialConfig.hideInMoodboard = true;
                    parent.surfaces = parent.surfaces.map(e => e.placement.name === point.configuration.placement.name ? materialConfig : e);
                    setConfiguration(configuration.clone());
                }
            }
        }

        setHasChanges(true);
        return true;
    }, [configuration, metadata, odataFilters]);


    return (
        <BomDesignerContext.Provider
            value={{
                loading,
                configuration,
                points,
                changeProduct,
                hasChanges,
                isNew,
                save,
                load,
                createNew,
                createFromConfig,
                editMetadata,
                title,
                setTitle,
                tags,
                addTag,
                removeTag,
                metadata,
                addMetadata,
                removeMetadata,
                addExternalPoint,
                removeExternalPoint,
                isEnabled,
                setIsEnabled,
            }}
        >
            {children}
        </BomDesignerContext.Provider>
    );
};